import {
  Authenticator,
  CheckboxField,
  Flex,
  Heading,
  Image,
  ToggleButton,
  useAuthenticator,
  View,
} from "@aws-amplify/ui-react";
import { signUp } from "aws-amplify/auth";
import { useEffect, useState } from "react";

// Have to use stupid global variable because Amplify Authenticator doesnt allow you to control form state
export type SignUpCustomAttributes = { role?: string; news?: boolean };
type SignUpCustomAttributesKeys = keyof SignUpCustomAttributes;
let signUpCustomAttributes: SignUpCustomAttributes = {};

export const WoofapyAuthenticator = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  useEffect(() => {
    signUpCustomAttributes = {};
  }, []);

  return (
    <Authenticator
      initialState="signIn"
      components={{
        Header() {
          return (
            <View
              as="div"
              padding="2rem"
              textAlign="center"
              backgroundColor="var(--amplify-colors-background-primary)"
            >
              <Flex direction="column" alignItems="center" gap="1rem">
                <Image
                  style={{ borderRadius: "20%" }}
                  alt="Woofapy Logo"
                  src="/favicons/favicon-96x96.png"
                  width="64px"
                  height="64px"
                />
                <Heading level={3} color="var(--mantine-color-black)">
                  Welcome to Woofapy
                </Heading>
              </Flex>
            </View>
          );
        },
        SignUp: {
          FormFields() {
            const [customAttributes, setCustomAttributes] =
              useState<SignUpCustomAttributes>(signUpCustomAttributes);
            const { validationErrors } = useAuthenticator();

            // Combine setRole and setNews into setAttribute
            const setAttribute = <K extends SignUpCustomAttributesKeys>(
              key: K,
              value: SignUpCustomAttributes[K],
            ) => {
              const updated = { ...customAttributes, [key]: value };
              setCustomAttributes(updated);
              signUpCustomAttributes = updated;
            };

            const termsLink = (
              <a
                href={
                  customAttributes.role === "trainer"
                    ? "/terms-trainer-2025-07-21.html"
                    : "/terms-owner-2025-07-21.html"
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </a>
            );

            const privacyPolicyLink = (
              <a
                href="/privacy-policy-2025-07-21.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            );

            return (
              <>
                <ToggleButton
                  variation="primary"
                  isPressed={customAttributes.role === "owner"}
                  onChange={() => setAttribute("role", "owner")}
                >
                  I am a dog owner
                </ToggleButton>
                <ToggleButton
                  variation="primary"
                  isPressed={customAttributes.role === "trainer"}
                  onChange={() => setAttribute("role", "trainer")}
                >
                  I am a dog trainer
                </ToggleButton>
                {customAttributes.role && (
                  <>
                    <Authenticator.SignUp.FormFields />
                    <CheckboxField
                      errorMessage={validationErrors.acknowledgement as string}
                      hasError={!!validationErrors.acknowledgement}
                      name="acknowledgement"
                      label={
                        <span>
                          I agree with the {termsLink} and {privacyPolicyLink}.
                        </span>
                      }
                    />
                    <CheckboxField
                      name="news"
                      label={
                        "I would like to receive news and updates about Woofapy."
                      }
                      checked={!!customAttributes.news}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setAttribute("news", e.target.checked)
                      }
                    />
                  </>
                )}
              </>
            );
          },
        },
      }}
      services={{
        async handleSignUp(input) {
          if (!signUpCustomAttributes.role) {
            throw new Error(
              `Please select whether you are an owner or a trainer`,
            );
          }

          return signUp({
            ...input,
            options: {
              ...input.options,
              userAttributes: {
                ...input.options?.userAttributes,
                "custom:role": signUpCustomAttributes.role,
                "custom:news": signUpCustomAttributes.news ? "true" : "false",
              },
            },
          });
        },

        async validateCustomSignUp(formData) {
          if (!formData.acknowledgement) {
            return {
              acknowledgement: "You must agree to the Terms and Conditions.",
            };
          }
        },
      }}
    >
      {children}
    </Authenticator>
  );
};
